/**
 * Modal de criação de persona (Brief 4 · Tela 2 · PR6 · gate `persona-author`).
 *
 * Form com os campos OBRIGATÓRIOS do `CreatePersonaDto` (nome · vertical · tom de
 * voz · vocabulário · limites éticos). Reusa os editores já existentes da tela:
 * inputs de texto para o tom (voiceProfile é JSON de strings livres, sem enum) e
 * `PersonaChipEditor` para vocabulário/ética. A persona nasce `Ativa` (o POST não
 * aceita `status` · R15 ao vivo) — a copy diz isso (zero dado falso). Os 3 objetos
 * podem nascer vazios; o Gestor ajusta os detalhes (exemplos/side/adversarial) no
 * editor logo em seguida. Renderiza dentro do `.personas-screen` para herdar os
 * estilos `pe-*` escopados (o Modal da DS é inline, não portal). Espelha
 * `PlaybookCreateModal` (Brief 4 · Tela 1).
 */

import { Modal } from '@lume/ui-components';
import { useEffect, useState } from 'react';

import { ApiError } from '../../lib/api-client';

import {
  buildCreatePersonaPayload,
  CREATE_VERTICALS,
  validateCreatePersonaForm,
} from './persona-create';
import { verticalLabel, VOICE_DIMENSION_LABELS } from './persona-labels';
import { PersonaChipEditor } from './sections/PersonaChipEditor';
import { useCreatePersona } from './use-personas';

import type { CreatePersonaFormErrors } from './persona-create';
import type { PersonaDto, PersonaVertical, PersonaVoiceProfile } from './types';

const EMPTY_VOICE: PersonaVoiceProfile = { formality: '', warmth: '', pace: '' };

const VOICE_PLACEHOLDERS: Record<keyof PersonaVoiceProfile, string> = {
  formality: 'ex.: neutro',
  warmth: 'ex.: acolhedor',
  pace: 'ex.: normal',
};

const VOICE_DIMENSIONS: (keyof PersonaVoiceProfile)[] = ['formality', 'warmth', 'pace'];

export function PersonaCreateModal({
  isOpen,
  onClose,
  onCreated,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (persona: PersonaDto) => void;
}): JSX.Element {
  const [name, setName] = useState('');
  const [vertical, setVertical] = useState<PersonaVertical>('support');
  const [voiceProfile, setVoiceProfile] = useState<PersonaVoiceProfile>(EMPTY_VOICE);
  const [avoidedTerms, setAvoidedTerms] = useState<string[]>([]);
  const [redirectTopics, setRedirectTopics] = useState<string[]>([]);
  const [toneLimits, setToneLimits] = useState<string[]>([]);
  const [errors, setErrors] = useState<CreatePersonaFormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const mutation = useCreatePersona();

  // Reseta o form quando o modal fecha (o componente fica montado; sem isto a
  // próxima abertura traria os campos da criação anterior).
  useEffect(() => {
    if (!isOpen) {
      setName('');
      setVertical('support');
      setVoiceProfile(EMPTY_VOICE);
      setAvoidedTerms([]);
      setRedirectTopics([]);
      setToneLimits([]);
      setErrors({});
      setSubmitError(null);
    }
  }, [isOpen]);

  const handleConfirm = (): void => {
    if (mutation.isPending) return;
    const fieldErrors = validateCreatePersonaForm({ name });
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;
    setSubmitError(null);
    mutation.mutate(
      buildCreatePersonaPayload({
        name,
        vertical,
        voiceProfile,
        vocabulary: { preferredTerms: [], avoidedTerms },
        ethicalBoundaries: { redirectTopics, toneLimits },
      }),
      {
        onSuccess: (created) => {
          onCreated(created);
        },
        onError: (err) => {
          if (err instanceof ApiError && err.status === 409) {
            setSubmitError('Já existe uma persona com esse nome. Escolha outro nome.');
            return;
          }
          setSubmitError('Não foi possível criar a persona. Tente novamente.');
        },
      },
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Nova persona"
      confirmLabel={mutation.isPending ? 'Criando…' : 'Criar persona'}
      cancelLabel="Cancelar"
      onConfirm={handleConfirm}
    >
      <div className="pe-create-form">
        <div className="pe-field">
          <label className="pe-field__label" htmlFor="pe-create-name">
            Nome
          </label>
          <input
            id="pe-create-name"
            className="pe-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="ex: Suporte Técnico"
            maxLength={120}
          />
          {errors.name !== undefined && (
            <span className="pe-field__error" role="alert">
              {errors.name}
            </span>
          )}
        </div>

        <div className="pe-field">
          <label className="pe-field__label" htmlFor="pe-create-vertical">
            Vertical
          </label>
          <select
            id="pe-create-vertical"
            className="pe-select"
            value={vertical}
            onChange={(e) => setVertical(e.target.value as PersonaVertical)}
          >
            {CREATE_VERTICALS.map((v) => (
              <option key={v} value={v}>
                {verticalLabel(v)}
              </option>
            ))}
          </select>
        </div>

        <fieldset className="pe-create-form__group">
          <legend className="pe-field__label">Tom de voz</legend>
          <div className="pe-grid-3">
            {VOICE_DIMENSIONS.map((dim) => (
              <div className="pe-field" key={dim}>
                <label className="pe-field__label" htmlFor={`pe-create-voice-${dim}`}>
                  {VOICE_DIMENSION_LABELS[dim]}
                </label>
                <input
                  id={`pe-create-voice-${dim}`}
                  type="text"
                  className="pe-input"
                  value={voiceProfile[dim]}
                  placeholder={VOICE_PLACEHOLDERS[dim]}
                  onChange={(e) => setVoiceProfile({ ...voiceProfile, [dim]: e.target.value })}
                />
              </div>
            ))}
          </div>
        </fieldset>

        <fieldset className="pe-create-form__group">
          <legend className="pe-field__label">Vocabulário</legend>
          {/* 06/Ago/2026 — «Termos preferidos» removido: a linha «Prefira os termos» deixou de
              ser emitida ao modelo (era meta sem contexto; vide `SectionVocabularyEdit`). O campo
              segue no contrato, enviado vazio, para nada ficar `undefined` no banco. */}
          <div className="pe-field">
            <span className="pe-field__sublabel">Termos a evitar</span>
            <PersonaChipEditor
              values={avoidedTerms}
              onChange={setAvoidedTerms}
              placeholder="Adicionar termo a evitar"
              ariaLabel="Adicionar termo a evitar"
            />
          </div>
        </fieldset>

        <fieldset className="pe-create-form__group">
          <legend className="pe-field__label">Limites éticos</legend>
          <div className="pe-field">
            <span className="pe-field__sublabel">Tópicos de redirecionamento</span>
            <PersonaChipEditor
              values={redirectTopics}
              onChange={setRedirectTopics}
              placeholder="Adicionar tópico de redirecionamento"
              ariaLabel="Adicionar tópico de redirecionamento"
            />
          </div>
          <div className="pe-field">
            <span className="pe-field__sublabel">Limites de tom</span>
            <PersonaChipEditor
              values={toneLimits}
              onChange={setToneLimits}
              placeholder="Adicionar limite de tom"
              ariaLabel="Adicionar limite de tom"
            />
          </div>
        </fieldset>

        <p className="pe-create-form__note">
          A persona será criada como <strong>Ativa</strong>. Você poderá ajustar exemplos, lado
          representado, perfil adversarial e demais detalhes no editor logo em seguida.
        </p>

        {submitError !== null && (
          <p className="pe-alert" role="alert">
            {submitError}
          </p>
        )}
      </div>
    </Modal>
  );
}
